import appSettings from '../config/settings';
import versionConfig from './scheduleServiceProcessVersions.json';

const {
  processName,
  latestProcessVersion,
  firstHistoricalProcessVersion,
  latestEquivalentProcessVersions,
  capabilityVersionOverridesByMarketplaceClientId,
  cancellationTransitionMinimumVersions,
} = versionConfig;

const getConfiguredCapabilityVersion = (
  processVersion,
  marketplaceClientId = appSettings.sdk.clientId
) => {
  const marketplaceOverrides =
    capabilityVersionOverridesByMarketplaceClientId[marketplaceClientId] || {};
  const marketplaceCapabilityVersion = Number(marketplaceOverrides[processVersion]);

  if (Number.isInteger(marketplaceCapabilityVersion)) {
    return marketplaceCapabilityVersion;
  }

  return latestEquivalentProcessVersions.includes(processVersion)
    ? latestProcessVersion
    : processVersion;
};

export const SCHEDULE_SERVICE_PROCESS_NAME = processName;
export const LATEST_SCHEDULE_SERVICE_PROCESS_VERSION = latestProcessVersion;
export const SCHEDULE_SERVICE_CANCELLATION_TRANSITIONS = Object.keys(
  cancellationTransitionMinimumVersions
);

export const SUPPORTED_SCHEDULE_SERVICE_PROCESS_VERSIONS = [
  ...latestEquivalentProcessVersions,
  ...Array.from(
    { length: latestProcessVersion - firstHistoricalProcessVersion + 1 },
    (_, index) => firstHistoricalProcessVersion + index
  ),
];
export const SCHEDULE_SERVICE_CANCELLATION_PROCESS_VERSIONS = SUPPORTED_SCHEDULE_SERVICE_PROCESS_VERSIONS.filter(
  processVersion => {
    const capabilityVersion = getConfiguredCapabilityVersion(processVersion);
    return Object.values(cancellationTransitionMinimumVersions).some(
      minimumVersion => capabilityVersion >= minimumVersion
    );
  }
);

export const getScheduleServiceCapabilityVersion = (candidateProcessName, processVersion) => {
  const numericVersion = Number(processVersion);
  if (
    candidateProcessName !== processName ||
    !SUPPORTED_SCHEDULE_SERVICE_PROCESS_VERSIONS.includes(numericVersion)
  ) {
    return null;
  }

  return getConfiguredCapabilityVersion(numericVersion);
};

export const isScheduleServiceProcessVersion = (candidateProcessName, processVersion) =>
  getScheduleServiceCapabilityVersion(candidateProcessName, processVersion) !== null;

export const isScheduleServiceCancellationTransition = (
  candidateProcessName,
  processVersion,
  transitionName
) =>
  isScheduleServiceProcessVersion(candidateProcessName, processVersion) &&
  getScheduleServiceCapabilityVersion(candidateProcessName, processVersion) >=
    cancellationTransitionMinimumVersions[transitionName];

export const isScheduleServiceVersionAtLeast = (
  candidateProcessName,
  processVersion,
  minimumVersion
) => {
  const capabilityVersion = getScheduleServiceCapabilityVersion(
    candidateProcessName,
    processVersion
  );
  return capabilityVersion !== null && capabilityVersion >= minimumVersion;
};

export const isScheduleServiceVersionBetween = (
  candidateProcessName,
  processVersion,
  minimumVersion,
  maximumVersion
) => {
  const capabilityVersion = getScheduleServiceCapabilityVersion(
    candidateProcessName,
    processVersion
  );
  return (
    capabilityVersion !== null &&
    capabilityVersion >= minimumVersion &&
    capabilityVersion <= maximumVersion
  );
};
